import { V4UUID, workspaceStateAsync } from '@/firebase/database';
import { Service } from '../nodesCanvasModule/components/storeClasses/service';
import { Topic } from '../nodesCanvasModule/components/storeClasses/topic';
import { PackageT } from '@/modules/nodesCanvasModule/components/storeClasses/packageT';
import { primitiveMessages } from '../nodesCanvasModule/store';
import { defineStore } from 'pinia';
import {
  getAllPackagesInWorkspace,
  savePackage
} from '@/firebase/databaseDependencyInjectables';
import { Message } from '../nodesCanvasModule/components/storeClasses/message';
import { Node } from '../nodesCanvasModule/components/storeClasses/node';
import { removeElement, toCamelCase } from './components/helperMethods';
import { updatePackageLaunchFileObject } from '@/codeGeneration/launchFileGen';
import { CanvasItem } from '../nodesCanvasModule/components/storeClasses/canvasObjects';

export interface GlobalStoreState {
  packages: Array<PackageT>;
  workspaceId: V4UUID | null;
  workspaceName: string;
  loaded: boolean;
}

export const useGlobalStore = defineStore({
  id: 'globalStore',
  state: (): GlobalStoreState => {
    return {
      packages: [],
      workspaceId: null,
      workspaceName: '',
      loaded: false
    };
  },
  actions: {
    savePackage(pkg: PackageT) {
      const prevPkg = this.packages.find((tempPkg) => tempPkg.id === pkg.id);
      if (prevPkg) {
        removeElement(this.packages, prevPkg);
      }
      this.packages.push(pkg);
    },
    updatePackageData(pkg: PackageT) {
      console.log('adding launch file to packatge');
      updatePackageLaunchFileObject(pkg);
    },
    /**
     * Loads a workspace and all its relevant packages
     * @param workspaceId
     */
    async loadWorkspace(workspaceId: V4UUID) {
      this.workspaceId = workspaceId;
      let allPackagesRecords = getAllPackagesInWorkspace(workspaceId);
      let workspaceDetails = await workspaceStateAsync(workspaceId);
      if (!allPackagesRecords) return;
      this.packages = Object.entries(allPackagesRecords).map(
        (entry) => entry[1]
      );
      if (!workspaceDetails || !workspaceDetails.info) return;
      this.workspaceName = workspaceDetails.info.name;
      this.loaded = true;
    },
    getPackage(packageId: V4UUID): PackageT | undefined {
      return this.packages.find((pkg) => pkg.id === packageId);
    },
    getPackages() {
      return this.packages;
    },
    getPackageFromName(name: string): PackageT | undefined {
      return this.packages.find((pkg) => pkg.name === name);
    },
    /**
     * Gets all nodes in the workspace
     */
    getNodes(): Node[] {
      return this.packages.reduce((accumulator: Node[], pkg: PackageT) => {
        if (!pkg.nodes) return accumulator;
        return [...accumulator, ...pkg.nodes] as Node[];
      }, []);
    },
    /**
     * Gets all topics in the workspace
     */
    getTopics(): Topic[] {
      return this.packages.reduce((accumulator: Topic[], pkg: PackageT) => {
        if (!pkg.topics) return accumulator;
        return [...accumulator, ...pkg.topics] as Topic[];
      }, []);
    },

    /**
     * Gets all services in the workspace
     */
    getServices(): Service[] {
      return this.packages.reduce((accumulator: Service[], pkg: PackageT) => {
        if (!pkg.services) return accumulator;
        return [...accumulator, ...pkg.services] as Service[];
      }, []);
    },
    /**
     * Gets all messages in the workspace
     */
    getMessages(): Message[] {
      return this.packages
        .reduce((accumulator: Message[], pkg: PackageT) => {
          if (!pkg.msgs) return accumulator;
          return [...accumulator, ...pkg.msgs] as Message[];
        }, [])
        .concat(primitiveMessages);
    },
    /**
     * Gets the node with the given id. returns undefined if no such node exists
     */
    getNode(id: V4UUID): Node | undefined {
      return this.getNodes().find((node) => node.id === id);
    },
    /**
     * Gets the topic with the given id. returns undefined if no such topic exists
     */
    getTopic(id: V4UUID): Topic | undefined {
      return this.getTopics().find((topic) => topic.id === id);
    },

    /**
     * Gets the service with the given id. returns undefined if no such service exists
     */
    getService(id: V4UUID): Service | undefined {
      return this.getServices().find((service) => service.id === id);
    },
    /**
     * Gets the message with the given id. returns undefined if no such message exists
     */
    getMessage(id: V4UUID): Message | undefined {
      return this.getMessages().find((msg) => msg.id === id);
    },
    /**
     * creates a new node and adds to package with given id. returns the new node
     * @param pkgId
     * @param nodeData
     * @returns
     */
    createNodeInPkg(pkgId: V4UUID, nodeData: any): Node {
      const pkg = this.getPackage(pkgId);
      const node = Node.fromJSON(nodeData);
      if (pkg) pkg.nodes.push(node);
      else
        console.warn('GlobalStore createNodeInPackage failed to find package');
      return node;
    },
    /**
     * creates a new topic and adds to package with given id. returns the new topic
     * @param pkgId
     * @param topicData
     */
    createTopicInPkg(pkgId: V4UUID, topicData: any): Topic {
      const pkg = this.getPackage(pkgId);
      const topic = Topic.fromJSON(topicData);
      if (pkg) pkg.topics.push(topic);
      else
        console.warn('GlobalStore createTopicInPackage failed to find package');
      return topic;
    },
    /**
     * creates a new service and adds to package with given id. returns the new service
     * @param pkgId
     * @param serviceData
     */
    createServiceInPkg(pkgId: V4UUID, serviceData: any): Service {
      const pkg = this.getPackage(pkgId);
      const service = Service.fromJSON(serviceData);
      if (pkg) pkg.services.push(service);
      else
        console.warn(
          'GlobalStore createServiceInPackage failed to find package'
        );
      return service;
    },
    /**
     * creates a new message and adds to package with given id. returns the new message
     * @param pkgId
     * @param messageData
     */
    createMessageInPkg(pkgId: V4UUID, messageData: any): Message {
      const pkg = this.getPackage(pkgId);
      const message = Message.fromJSON(messageData);
      if (pkg) pkg.msgs.push(message);
      else
        console.warn(
          'GlobalStore createMessageInPackage failed to find package'
        );
      return message;
    },
    /**
     * Gets either the node service, topic, message, or package,
     * represented by the Id
     * @param id
     * @returns
     */
    getElemById(
      id: V4UUID
    ): Node | Service | Topic | Message | PackageT | undefined {
      if (this.getNode(id)) return this.getNode(id);
      if (this.getTopic(id)) return this.getTopic(id);
      if (this.getMessage(id)) return this.getMessage(id);
      if (this.getPackage(id)) return this.getPackage(id);
      return undefined;
    },
    /**
     * returns a canvas item by the id
     * (node, service, or topic)
     * @param id
     * @returns
     */
    getCanvasElemById(id: V4UUID): Node | Service | Topic | undefined {
      if (this.getNode(id)) return this.getNode(id);
      if (this.getService(id)) return this.getService(id);
      if (this.getTopic(id)) return this.getTopic(id);
    },
    /**
     * Gets the type of the element (using typeof) that corresponds with
     * the given id. Id can represent a Node, Topic, Message or PackageT
     * @param id
     * @returns 'Node' | 'Topic' | 'Message' | 'Package' | 'Service' | undefined
     */
    getTypeOfElemById(id: V4UUID): string | undefined {
      if (this.getNode(id)) return 'Node';
      if (this.getTopic(id)) return 'Topic';
      if (this.getService(id)) return 'Service';
      if (this.getMessage(id)) return 'Message';
      if (this.getPackage(id)) return 'Package';
      return undefined;
    },
    saveData() {
      if (!this.workspaceId) return;
      for (let pkg of this.packages) {
        this.updatePackageData(pkg);
        savePackage(this.workspaceId, pkg.id, pkg);
      }
    },
    /**
     * Retrieves the given element based on its file path.
     * Assumes path starts with workspace then package folder
     * @param filePath
     */
    getElemFromFilePath(
      filePath: string
    ): Node | Topic | Service | Message | PackageT | undefined {
      const fileName = filePath.split('/').reverse()[0];
      if (filePath.split('/').length >= 3) {
        const packageName = filePath.split('/')[1];
        const pkg = this.packages.find(
          (currpkg) => currpkg.name == toCamelCase(packageName)
        );
        if (pkg) {
          if (filePath.endsWith('.launch.py')) {
            return pkg;
          } else if (filePath.endsWith('.py')) {
            return pkg.nodes.find(
              (node) => toCamelCase(node.name) + '.py' === fileName
            );
          } else if (filePath.endsWith('.cpp') || filePath.endsWith('.hpp')) {
            return pkg.nodes.find(
              (node) =>
                toCamelCase(node.name) + '.cpp' === fileName ||
                toCamelCase(node.name) + '.hpp' === fileName
            );
          } else if (filePath.endsWith('.msg')) {
            return pkg.msgs.find(
              (msg) => toCamelCase(msg.name) + '.msg' === fileName
            );
          } else if (filePath.endsWith('.srv')) {
            return pkg.services.find(
              (srv) => toCamelCase(srv.name) + '.srv' === fileName
            );
          }
          return pkg;
        }
      }

      if (filePath.endsWith('.launch.py')) {
        const packageName = filePath.split('/').reverse()[0].split('.')[0];
        return this.packages.find(
          (currpkg) => currpkg.name == toCamelCase(packageName)
        );
      } else if (filePath.endsWith('.py')) {
        return this.getNodes().find(
          (node) => toCamelCase(node.name) + '.py' === fileName
        );
      } else if (filePath.endsWith('.cpp') || filePath.endsWith('.hpp')) {
        return this.getNodes().find(
          (node) =>
            toCamelCase(node.name) + '.cpp' === fileName ||
            toCamelCase(node.name) + '.hpp' === fileName
        );
      } else if (filePath.endsWith('.msg')) {
        return this.getMessages().find(
          (msg) => toCamelCase(msg.name) + '.msg' === fileName
        );
      } else if (filePath.endsWith('.srv')) {
        return this.getServices().find(
          (srv) => toCamelCase(srv.name) + '.srv' === fileName
        );
      }
    },
    getElemPackage(elemId: V4UUID): PackageT | undefined {
      for (let pkg of this.packages) {
        if (this.checkElemInPackage(pkg.id, elemId)) return pkg;
      }
      return undefined;
    },
    checkElemInPackage(pkgId: V4UUID, elemId: V4UUID): boolean {
      let pkg = this.getPackage(pkgId);
      if (!pkg) return false;
      let elemType = this.getTypeOfElemById(elemId);
      if (elemType === 'Node') {
        return (
          pkg.nodes.filter((node: Node) => node.id === elemId).length !== 0
        );
      } else if (elemType === 'Topic') {
        return (
          pkg.topics.filter((topic: Topic) => topic.id === elemId).length !== 0
        );
      } else if (elemType === 'Service') {
        return (
          pkg.services.filter((service: Service) => service.id === elemId)
            .length !== 0
        );
      } else if (elemType === 'Message') {
        return (
          pkg.msgs.filter((message: Message) => message.id === elemId)
            .length !== 0
        );
      } else if (elemType === 'Package') {
        return elemId === pkgId;
      }
      return false;
    },
    /**
     * Deletes a given node, topic, service or message from their given ID
     * NOTE: Does not function for packages
     * @param id
     */
    deleteElemById(id: V4UUID) {
      let type = this.getTypeOfElemById(id);
      let elemPackage = this.getElemPackage(id);
      if (!elemPackage) return;
      if (type === 'Node') {
        elemPackage.nodes = elemPackage.nodes.filter(
          (node: Node) => node.id !== id
        );
        for (let service of this.getServices()) {
          if (service.server === id) service.server = undefined;
          service.clients = service.clients.filter(
            (clientId) => clientId !== id
          );
        }
      } else if (type === 'Topic') {
        elemPackage.topics = elemPackage.topics.filter(
          (topic: Topic) => topic.id !== id
        );
        // remove all references to topic from other nodes
        for (let node of this.getNodes()) {
          node.subscribers = node.subscribers.filter((sub) => sub !== id);
          node.publishers = node.publishers.filter((pub) => pub !== id);
        }
      } else if (type === 'Service') {
        elemPackage.services = elemPackage.services.filter(
          (srv) => srv.id !== id
        );
      } else if (type === 'Message') {
        elemPackage.msgs = elemPackage.msgs.filter((msg) => msg.id !== id);
        // sets all topics with matching message type to string message
        for (let topic of this.getTopics()) {
          if (topic.msgType === id) {
            topic.msgType = primitiveMessages[0].id;
          }
        }
        // removes any paramaters with this message type
        for (let msg of this.getMessages()) {
          msg.fields = msg.fields.filter((field) => field.id !== id);
        }
        for (let srv of this.getServices()) {
          srv.requestList = srv.requestList.filter((field) => field.id !== id);
          srv.responseList = srv.responseList.filter(
            (field) => field.id !== id
          );
        }
      }
    },
    isCustomMessage(msgId: V4UUID): boolean {
      if (primitiveMessages.find((msg) => msg.id === msgId)) return false;
      const msgPackage = this.getElemPackage(msgId);
      if (!msgPackage || msgPackage.robotPackage) return false;
      return true;
    }
  }
});
